import { useEffect, useState } from 'react';
import { Amplify } from 'aws-amplify';
import {
  signIn,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  AuthUser,
  confirmSignIn
} from 'aws-amplify/auth';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import TeamsArchive from './components/TeamsArchive';
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './components/admin/AdminDashboard';
import TenantManagement from './components/admin/TenantManagement';
import UserManagement from './components/admin/UserManagement';
import BrandingSettings from './components/admin/BrandingSettings';
import AuditLogs from './components/admin/AuditLogs';
import { useAdminAccess } from './hooks/useAdminAccess';
import { TenantProvider, useTenant } from './context/TenantContext';
import './App.css';

// Tenant configuration - maps subdomains to Cognito app clients
const TENANT_CONFIG: Record<string, { clientId: string; name: string }> = {
  'tlg': {
    clientId: '3eev2tvkp36cvcibf4g4b3k6g9',
    name: 'The Leaders Group'
  },
  'simplicity': {
    clientId: '3eev2tvkp36cvcibf4g4b3k6g9',
    name: 'Simplicity Group'
  },
  'default': {
    clientId: '3eev2tvkp36cvcibf4g4b3k6g9',
    name: 'ArchiveAbyss'
  }
};

// Detect tenant from hostname
function getTenantFromHostname(): string {
  const hostname = window.location.hostname;

  if (hostname.includes('.archiveabyss.com')) {
    const subdomain = hostname.split('.')[0];
    if (TENANT_CONFIG[subdomain]) {
      return subdomain;
    }
  }

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const params = new URLSearchParams(window.location.search);
    const tenantParam = params.get('tenant');
    if (tenantParam && TENANT_CONFIG[tenantParam]) {
      return tenantParam;
    }
  }

  return 'default';
}

const currentTenant = getTenantFromHostname();
const tenantConfig = TENANT_CONFIG[currentTenant];

// Configure Amplify - using the original client for direct signIn
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: 'us-east-1_RL02y4eJS',
      userPoolClientId: tenantConfig.clientId
    }
  }
});

function ProtectedAdminRoute({ children }: { children: React.ReactNode }) {
  const { isAdmin, loading } = useAdminAccess();

  if (loading) {
    return <div className="admin-loading">Checking access...</div>;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

interface LoginPageProps {
  onLoginSuccess: () => void;
}

function LoginPage({ onLoginSuccess }: LoginPageProps) {
  const { config } = useTenant();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [requiresNewPassword, setRequiresNewPassword] = useState(false);

  // Get branding from tenant config
  const branding = config?.branding;
  const displayName = branding?.company_name || tenantConfig.name;
  const logoUrl = branding?.logo_url;
  const primaryColor = branding?.primary_color || '#7c3aed';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await signIn({ username: username.toLowerCase().trim(), password });

      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setRequiresNewPassword(true);
        setIsLoading(false);
        return;
      }

      if (result.isSignedIn) {
        onLoginSuccess();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Sign in failed';
      if (errorMessage.includes('Incorrect username or password')) {
        setError('Invalid email or password');
      } else if (errorMessage.includes('User does not exist')) {
        setError('No account found with this email');
      } else {
        setError(errorMessage);
      }
      setIsLoading(false);
    }
  };

  const handleNewPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);

    try {
      const result = await confirmSignIn({ challengeResponse: newPassword });
      if (result.isSignedIn) {
        onLoginSuccess();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set new password');
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-header" style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)` }}>
          {logoUrl ? (
            <img src={logoUrl} alt={displayName} className="login-logo" />
          ) : (
            <h1 className="login-title">{displayName}</h1>
          )}
          <p className="login-subtitle">Message Archive</p>
        </div>

        <div className="login-body">
          {error && (
            <div className="login-error">
              {error}
            </div>
          )}

          {!requiresNewPassword ? (
            <form onSubmit={handleSubmit}>
              <div className="login-field">
                <label htmlFor="username">Email</label>
                <input
                  id="username"
                  type="email"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="you@company.com"
                  required
                  autoComplete="email"
                  autoFocus
                />
              </div>

              <div className="login-field">
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  autoComplete="current-password"
                />
              </div>

              <button
                type="submit"
                className="login-btn"
                disabled={isLoading}
                style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)` }}
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleNewPasswordSubmit}>
              <p className="login-message">
                Please set a new password to continue.
              </p>

              <div className="login-field">
                <label htmlFor="newPassword">New Password</label>
                <input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  autoComplete="new-password"
                  autoFocus
                />
              </div>

              <div className="login-field">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  autoComplete="new-password"
                />
              </div>

              <button
                type="submit"
                className="login-btn"
                disabled={isLoading}
                style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)` }}
              >
                {isLoading ? 'Setting password...' : 'Set Password & Continue'}
              </button>
            </form>
          )}
        </div>

        <div className="login-footer">
          <p>Powered by ArchiveAbyss</p>
        </div>
      </div>
    </div>
  );
}

function AuthenticatedApp({ user, onSignOut }: { user: AuthUser; onSignOut: () => void }) {
  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/"
          element={<TeamsArchive user={user} signOut={onSignOut} />}
        />
        <Route
          path="/admin"
          element={
            <ProtectedAdminRoute>
              <AdminLayout user={user} signOut={onSignOut}>
                <AdminDashboard />
              </AdminLayout>
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/tenants"
          element={
            <ProtectedAdminRoute>
              <AdminLayout user={user} signOut={onSignOut}>
                <TenantManagement />
              </AdminLayout>
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedAdminRoute>
              <AdminLayout user={user} signOut={onSignOut}>
                <UserManagement />
              </AdminLayout>
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/branding"
          element={
            <ProtectedAdminRoute>
              <AdminLayout user={user} signOut={onSignOut}>
                <BrandingSettings />
              </AdminLayout>
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/audit-logs"
          element={
            <ProtectedAdminRoute>
              <AdminLayout user={user} signOut={onSignOut}>
                <AuditLogs />
              </AdminLayout>
            </ProtectedAdminRoute>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

function AppContent() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = async () => {
    try {
      const currentUser = await getCurrentUser();
      await fetchAuthSession();
      setUser(currentUser);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSuccess = async () => {
    await checkAuthState();
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setUser(null);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  if (loading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) {
    return <LoginPage onLoginSuccess={handleLoginSuccess} />;
  }

  return <AuthenticatedApp user={user} onSignOut={handleSignOut} />;
}

function App() {
  return (
    <TenantProvider>
      <AppContent />
    </TenantProvider>
  );
}

export default App;
